import { createContext, useContext, useMemo, useCallback, useState, useEffect } from 'react';
import { getDefaultBlockState, getDefaultFlowState, blocksApi, setupSocket } from '@dopt/javascript-common';
import { jsx } from 'react/jsx-runtime';
import { Logger } from '@dopt/logger';

var DoptContext = createContext({});

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise */


var __assign = function() {
    __assign = Object.assign || function __assign(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};

function createBlock(_a) {
    var uid = _a.uid, fetching = _a.fetching, blocks = _a.blocks, blockFields = _a.blockFields, blockIntention = _a.blockIntention;
    var block = fetching || !blocks[uid] ? getDefaultBlockState(uid, uid, -1) : blocks[uid];
    // overwrite the type of transitioned using generics
    var transitioned = block.transitioned;
    var transition = function () {
        var inputs = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            inputs[_i] = arguments[_i];
        }
        if (!fetching) {
            blockIntention(uid, inputs);
        }
    };
    var field = function (name, defaultValue) {
        var _a;
        var fieldMap = blockFields.get(block.uid);
        if (fetching || fieldMap == null) {
            return null;
        }
        var value = (_a = fieldMap.get(name)) === null || _a === void 0 ? void 0 : _a.value;
        return value != null
            ? value
            : defaultValue != null
                ? defaultValue
                : null;
    };
    return __assign(__assign({}, block), { transitioned: transitioned, field: field, transition: transition });
}

/**
 * A React hook for accessing a block's state and methods
 * corresponding to an intent-based API for manipulating
 * said block state.
 *
 * @example
 * ```tsx
 * import { useBlock } from "@dopt/react";
 * import { Modal } from "@your-company/modal";
 *
 * export function Application() {
 *   const [block, transition] = useBlock("HNWvcT78tyTwygnbzU6SW");
 *   const onClick = useCallback(() => {
 *     transition('default');
 *   }, [transition]);
 *   return (
 *     <main>
 *       <Modal isOpen={block.state.active}>
 *         <h1>👏 Welcome to our app!</h1>
 *         <p>This is your onboarding experience!</p>
 *         <button onClick={onClick}>Close me</button>
 *       </Modal>
 *     </main>
 *   );
 * }
 * ```
 *
 * @param id - one of {@link Block['sid']} | {@link Block['uid']}
 * this param accepts either the user defined identifier (sid) or the system created identifier (the uid)
 * @returns [{@link Block}, {@link BlockTransition}] the state of the block and methods to manipulate block state
 *
 */
function useBlock(id) {
    var _a = useContext(DoptContext), fetching = _a.fetching, blocks = _a.blocks, blockIntention = _a.blockIntention, log = _a.log, blockFields = _a.blockFields, blockUidBySid = _a.blockUidBySid;
    if (fetching) {
        log.info('Accessing block prior to initialization will return default block states.');
    }
    var uid = blockUidBySid.get(id) || id;
    var block = useMemo(function () {
        return createBlock({ uid: uid, fetching: fetching, blocks: blocks, blockFields: blockFields, blockIntention: blockIntention });
    }, [uid, fetching, blocks, blockFields]);
    return [block, block.transition];
}

/**
 * A React HOC for accessing block state and methods corresponding
 * to an intent-based API for maniuplating said state.
 *
 * @example
 * ```tsx
 *  import { withDopt } from '@dopt/react';
 *  import { WelcomeModal } from './welcome-modal';
 *
 *  export function Application() {
 *    const WelcomeModalWithDopt = withDopt(WelcomeModal, 'j0zExxZDVKCPXPzB2ZgpW');
 *    return (
 *      <main>
 *        <WelcomeModalWithDopt />
 *      </main>
 *    );
 *  }
 * ```
 *
 * @param Component - the React component you with to inject Dopt props into
 * @param id - one of {@link Block['sid']} | {@link Block['uid']}
 * this param accepts either the user defined identifier (sid) or the system created identifier (the uid)
 * @returns The original component with {@link Block} and {@link BlockTransition}
 * injected as props.
 *
 */
function withBlock(Component, id) {
    var displayName = Component.displayName || Component.name || 'Component';
    var ComponentWithDopt = function (props) {
        var _a = useBlock(id), block = _a[0], intent = _a[1];
        return jsx(Component, __assign({}, props, { block: block, intent: intent }));
    };
    ComponentWithDopt.displayName = "withBlock(".concat(displayName, ", ").concat(id, ")");
    return ComponentWithDopt;
}

/**
 * A React hook for accessing a flow's state and
 * methods corresponding to an intent-based API for manipulating
 * flow state.
 *
 * @example
 * ```tsx
 * import { useFlow } from "@dopt/react";
 * import { Modal } from "@your-company/modal";
 *
 * export function Application() {
 *   const [flow, intent] = useFlow("new-user-onboarding");
 *   return (
 *     <main>
 *       <Modal isOpen={flow.state.finished}>
 *         <h1>👏 Your onboarding has finished!</h1>
 *         <p>Want to reset? click the button below.</p>
 *         <button onClick={intent.reset}>Reset onboarding</button>
 *       </Modal>
 *     </main>
 *   );
 * }
 * ```
 *
 * @param sid - {@link Flow['sid']}
 * @returns [{@link Flow}, {@link FlowIntent}] the state of the flow and methods to manipulate flow state
 *
 */
function useFlow(sid) {
    var _a = useContext(DoptContext), fetching = _a.fetching, flows = _a.flows, flowBlocks = _a.flowBlocks, blocks = _a.blocks, blockIntention = _a.blockIntention, flowIntention = _a.flowIntention, log = _a.log, blockFields = _a.blockFields;
    if (fetching) {
        log.info('Accessing flow prior to initialization will return default block states.');
    }
    var version = useMemo(function () {
        if (fetching) {
            return -1;
        }
        var flow = flows[sid];
        if (flow == null) {
            log.error("\n        Error using `useFlow(".concat(sid, ")` - check your `flowVersions`\n        props to ensure `").concat(sid, "` and its version is specified there"));
            return -1;
        }
        return flow.version;
    }, [fetching, flows, sid]);
    var reset = useCallback(function () {
        if (!fetching) {
            flowIntention.reset(sid, version);
        }
    }, [fetching, flowIntention, sid, version]);
    var stop = useCallback(function () {
        if (!fetching) {
            flowIntention.stop(sid, version);
        }
    }, [fetching, flowIntention, sid, version]);
    var finish = useCallback(function () {
        if (!fetching) {
            flowIntention.finish(sid, version);
        }
    }, [fetching, flowIntention, sid, version]);
    var flow = useMemo(function () {
        var updatedFlow = fetching || !flows[sid] ? getDefaultFlowState(sid, version) : flows[sid];
        var updatedBlocks = (flowBlocks.get(sid) || []).map(function (uid) {
            return createBlock({ uid: uid, fetching: fetching, blocks: blocks, blockFields: blockFields, blockIntention: blockIntention });
        });
        return __assign(__assign({}, updatedFlow), { blocks: updatedBlocks });
    }, [fetching, sid, version, flows, flowBlocks, blocks, blockFields]);
    return [flow, { reset: reset, stop: stop, finish: finish }];
}

/**
 * A React HOC for accessing flow state and methods corresponding
 * to an intent-based API for maniuplating said state.
 *
 * @example
 * ```tsx
 *  import { withFlow } from '@dopt/react';
 *  import { WelcomeModal } from './welcome-modal';
 *
 *  export function Application() {
 *    const WelcomeModalWithDopt = withFlow('new-user-onboarding', 1);
 *    return (
 *      <main>
 *        <WelcomeModalWithDopt />
 *      </main>
 *    );
 *  }
 * ```
 *
 * @param Component - the React component you with to inject Dopt props into
 * @param sid - {@link Flow['sid']}
 * @returns The original component with {@link Flow} and {@link FlowIntent}
 * props injected in
 *
 */
function withFlow(Component) {
    var useFlowArgs = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        useFlowArgs[_i - 1] = arguments[_i];
    }
    var displayName = Component.displayName || Component.name || 'Component';
    var ComponentWithDopt = function (props) {
        var _a = useFlow.apply(void 0, useFlowArgs), flow = _a[0], intent = _a[1];
        return jsx(Component, __assign({}, props, { flow: flow, intent: intent }));
    };
    ComponentWithDopt.displayName = "withFlow(".concat(displayName, ", ").concat(useFlowArgs.join(', '), ")");
    return ComponentWithDopt;
}

/**
 * A React hook for accessing a container's state and
 * methods corresponding to an intent-based API for manipulating
 * said state.
 *
 * @example
 * ```tsx
 * import { useContainer } from "@dopt/react";
 * import { Modal } from "@your-company/modal";
 *
 * export function Application() {
 *   const container = useContainer("HNWvcT78tyTwygnbzU6SW");
 *   const onClick = useCallback(() => {
 *     container.transition('dismiss');
 *   }, [container]);
 *
 *   const activeChild = container.children[0].state.active ?
 *     container.children[0] : container.children[1];
 *
 *   const itemsComplete = container.children[0].state.active ?
 *     '0 items completed' : '1 item completed';
 *
 *   return (
 *     <main>
 *       <Modal isOpen={container.state.active}>
 *         <h1>{activeChild.field('title')}</h1>
 *         <p>{activeChild.field('body')}</p>
 *         <p>{itemsComplete}</p>
 *         <button onClick={onClick}>Close me</button>
 *       </Modal>
 *     </main>
 *   );
 * }
 * ```
 *
 * @param id - one of {@link Container['sid']} | {@link Container['uid']}
 * this param accepts either the user defined identifier (sid) or the system created identifier (the uid)
 * @returns {@link Container} the state of the container and methods to manipulate container state
 *
 */
function useContainer(id) {
    var _a = useContext(DoptContext), fetching = _a.fetching, blocks = _a.blocks, blockIntention = _a.blockIntention, log = _a.log, blockFields = _a.blockFields, blockUidBySid = _a.blockUidBySid;
    if (fetching) {
        log.info('Accessing container prior to initialization will return default block states.');
    }
    var uid = blockUidBySid.get(id) || id;
    var container = useMemo(function () {
        var container = createBlock({
            uid: uid,
            fetching: fetching,
            blocks: blocks,
            blockFields: blockFields,
            blockIntention: blockIntention,
        });
        var children = Object.values(blocks)
            .filter(function (_a) {
            var containerUid = _a.containerUid;
            return containerUid === container.uid;
        })
            .map(function (_a) {
            var uid = _a.uid;
            return createBlock({ uid: uid, fetching: fetching, blocks: blocks, blockFields: blockFields, blockIntention: blockIntention });
        });
        return __assign(__assign({}, container), { children: children });
    }, [uid, fetching, blocks, blockFields]);
    return container;
}

/**
 * A React hook for accessing whether Dopt has been initialized.
 *
 * Dopt-level initialization is defined as:
 * - all flows have been fetched
 * - Dopt's socket connection is ready
 * - all flows which need to be started have been started
 *
 * @remarks
 * Note, this hook does not check whether any initialization steps had errors.
 * Use {@link useFlowStatus} to check flow status, including failures,
 * at a more granular level.
 *
 * @returns A boolean, `true` if Dopt is initialized, `false` otherwise.
 */
function useDoptInitialized() {
    var flowStatuses = useContext(DoptContext).flowStatuses;
    var initialized = useMemo(function () {
        var statuses = Object.values(flowStatuses);
        return statuses.length > 0 && statuses.every(function (status) { return !status.pending; });
    }, [flowStatuses]);
    return initialized;
}

/**
 * A React hook for accessing a Flow's initialization status.
 *
 * Initialization is defined as:
 * - the flow has been fetched
 * - Dopt's socket connection is ready
 * - the flow has been started, if necessary
 *
 * @remarks Once initialized, the status will be marked `pending: false`.
 * If any parts of initialization fail, the status will additionally
 * have `failed: true`. If the `sid` doesn't match any flows, the function
 * will return `pending: true, failed: false`.
 *
 * @param sid - {@link Flow['sid']}
 *
 * @returns A flow's {@link FlowStatus}.
 */
function useFlowStatus(sid) {
    var flowStatuses = useContext(DoptContext).flowStatuses;
    var flowStatus = useMemo(function () { return flowStatuses[sid] || { pending: true, failed: false }; }, [sid, flowStatuses]);
    return flowStatus;
}

var PKG_VERSION = "2.1.0" ;
var PKG_NAME = "@dopt/react" ;
var URL_PREFIX = "https://blocks.dopt.com" ;

/**
 * A React context provider for accessing block state.
 *
 * Using {@link ProviderConfig}
 * @example
 * ```tsx
 *  import { DoptProvider } from '@dopt/react';
 *  import Application from './application';
 *
 *  export function Index() {
 *    return (
 *      <DoptProvider
 *        userId={userId}
 *        apiKey={blockAPIKey}
 *        flowVersions={{
 *          onboardingFlow: 3,
 *          upgradeFlow: 1
 *        }}
 *      >
 *        <Application />
 *      </DoptProvider>
 *    );
 *  }
 * ```
 *
 */
function DoptProvider(props) {
    var userId = props.userId, groupId = props.groupId, apiKey = props.apiKey, flowVersions = props.flowVersions, children = props.children, logLevel = props.logLevel, _a = props.optimisticUpdates, optimisticUpdates = _a === void 0 ? true : _a;
    var log = new Logger({ logLevel: logLevel, prefix: " ".concat(PKG_NAME, " ") });
    var _b = useState({}), flows = _b[0], setFlows = _b[1];
    var _c = useState(new Map()), flowBlocks = _c[0], setFlowBlocks = _c[1];
    var _d = useState({}), blocks = _d[0], setBlocks = _d[1];
    var _e = useState(new Map()), blockFields = _e[0], setBlockFields = _e[1];
    var _f = useState(new Map()), blockUidBySid = _f[0], setBlockSidMap = _f[1];
    var _g = useState(true), fetching = _g[0], setFetching = _g[1];
    var _h = useState(), socket = _h[0], setSocket = _h[1];
    var _j = useState({}), flowStatuses = _j[0], setFlowStatuses = _j[1];
    useEffect(function () {
        if (userId === undefined) {
            log.info('The `userId` prop is undefined. The SDK will partially initialize, returning defaults for any requested blocks until the `userId` prop becomes defined.');
        }
    }, [userId]);
    useEffect(function () {
        if (groupId === undefined) {
            log.info('The `groupId` prop is undefined. The SDK wont be able to target the entry conditions and update blocks if you are actively using groups properties.');
        }
    }, [groupId]);
    useEffect(function () {
        log.debug('<DoptProvider /> mounted');
        return function () { return log.debug('<DoptProvider /> unmounted'); };
    }, []);
    /*
     * Create the Blocks API Client
     */
    var _k = useMemo(function () {
        return blocksApi({
            apiKey: apiKey,
            userId: userId,
            groupId: groupId,
            logger: log,
            config: {
                urlPrefix: URL_PREFIX,
                packageVersion: PKG_VERSION,
                packageName: PKG_NAME,
            },
        });
    }, [userId, apiKey, groupId]), getFlow = _k.getFlow, flowIntent = _k.flowIntent, blockIntent = _k.blockIntent;
    useEffect(function () {
        // Avoid any fetching until the user is defined
        if (!userId) {
            return;
        }
        /*
         * Fetch all Flows based on the **in parallel**
         * provided (Flow['sid'], Flow['version'])
         * tuples (via the `flowVersions` props)
         */
        Promise.all(Object.entries(flowVersions).map(function (_a) {
            var sid = _a[0], version = _a[1];
            return getFlow({ sid: sid, version: version });
        }))
            .then(function (flows) {
            var _flows = {};
            var _flowBlocks = new Map();
            var _blocks = {};
            var _blockFields = new Map();
            var _blockUidBySid = new Map();
            flows.forEach(function (flow) {
                var _a, _b;
                _flows[flow.sid] = flow;
                _flowBlocks.set(flow.sid, ((_a = flow.blocks) === null || _a === void 0 ? void 0 : _a.map(function (_a) {
                    var uid = _a.uid;
                    return uid;
                })) || []);
                (_b = flow.blocks) === null || _b === void 0 ? void 0 : _b.forEach(function (block) {
                    _blocks[block.uid] = block;
                    _blockUidBySid.set(block.sid, block.uid);
                    _blockFields.set(block.uid, block.fields.reduce(function (map, field) {
                        return map.set(field.sid, field);
                    }, new Map()));
                });
            });
            setFlows(_flows);
            setFlowBlocks(_flowBlocks);
            setBlocks(_blocks);
            setBlockFields(_blockFields);
            setBlockSidMap(_blockUidBySid);
            /*
             * If we've made it here we can safely progress i.e. the
             * SDK has fetched correctly.
             */
            setFetching(false);
            log.info('Flows & Blocks fetching successful');
        })
            .catch(function (error) {
            log.error("\n            An error occurred while fetching blocks for the\n            flow versions specified: `".concat(JSON.stringify(flowVersions), "`\n          "));
        });
    }, [JSON.stringify(flowVersions), userId, groupId]);
    var updateBlockState = useCallback(function (updated) {
        return setBlocks(function (prevBlocks) { return (__assign(__assign({}, prevBlocks), updated)); });
    }, []);
    var updateFlowState = useCallback(function (flow) {
        setFlows(function (prev) {
            var _a;
            return __assign(__assign({}, prev), (_a = {}, _a[flow.sid] = flow, _a));
        });
        setFlowStatuses(function (previousFlowStatuses) {
            var _a;
            var _b;
            /*
             *  Some flow intents generate side effects (they start flows if not started),
             *  this tracks that those intents are propagated back to the client
             */
            if ((_b = previousFlowStatuses[flow.sid]) === null || _b === void 0 ? void 0 : _b.pending) {
                return __assign(__assign({}, previousFlowStatuses), (_a = {}, _a[flow.sid] = { pending: false, failed: false }, _a));
            }
            else {
                return previousFlowStatuses;
            }
        });
    }, []);
    var blocksSocketCallback = useCallback(function (updatedBlocks) {
        log.debug("The following blocks were updated and pushed from the server.\n".concat(Object.values(updatedBlocks)
            .map(function (block) { return JSON.stringify(block, null, 2); })
            .join('\n')));
        updateBlockState(updatedBlocks);
    }, [updateBlockState]);
    var flowSocketCallback = useCallback(function (updatedFlow) {
        log.debug("The following flow was updated and pushed from the server.\n".concat(JSON.stringify(updatedFlow, null, 2)));
        updateFlowState(updatedFlow);
    }, [updateFlowState]);
    useEffect(function () {
        var _socket = setupSocket({
            apiKey: apiKey,
            userId: userId,
            groupId: groupId,
            log: log,
            urlPrefix: URL_PREFIX,
        });
        if (!_socket) {
            return function () { };
        }
        _socket.on('ready', function () {
            log.debug("Socket is ready for user ".concat(userId, "; group ").concat(groupId, "; flows ").concat(Object.keys(flowVersions).join(' ')));
            setSocket(_socket);
        });
        return function () {
            log.debug("Closing socket for user ".concat(userId, "; group ").concat(groupId, "; flows ").concat(Object.keys(flowVersions).join(' ')));
            _socket.close();
        };
    }, [apiKey, userId, groupId]);
    useEffect(function () {
        if (!socket) {
            return;
        }
        if (!(Object.keys(blocks).length > 0) || !(Object.keys(flows).length > 0)) {
            return;
        }
        socket.on('blocks', blocksSocketCallback);
        socket.on('flow', flowSocketCallback);
        // Log a warning if we end up in the situation above
        if (socket.listeners('blocks').length > 1) {
            log.warn('Socket listeners to `blocks` are growing unexpectedly.');
        }
        if (socket.listeners('flow').length > 1) {
            log.warn('Socket listeners to `flow` are growing unexpectedly.');
        }
        for (var uid in blocks) {
            var version = blocks[uid].version;
            socket.emit('watch:block', uid, version);
            socket.on("".concat(uid, "_").concat(version), function (block) {
                updateBlockState(block);
            });
            if (socket.listeners("".concat(uid, "_").concat(version)).length > 1) {
                log.error("Socket listeners to `".concat(uid, "_").concat(version, "` are growing unexpectedly."));
            }
        }
        Object.values(flows).forEach(function (_a) {
            var sid = _a.sid, version = _a.version;
            socket.emit('watch:flow', sid, version);
            socket.on("".concat(sid, "_").concat(version), updateFlowState);
        });
    }, [
        JSON.stringify(Object.keys(blocks).sort()),
        JSON.stringify(Object.keys(flows).sort()),
        socket,
    ]);
    useEffect(function () {
        if (!fetching && socket) {
            setFlowStatuses(Object.values(flows).reduce(function (statuses, flow) {
                var pending = false;
                if (!flow.state.started) {
                    /*
                     * Start the Flow if it hasn't been started
                     */
                    pending = true;
                    flowIntent({
                        sid: flow.sid,
                        version: flow.version,
                        intent: 'start',
                    }).then(function (intentHasSideEffects) {
                        if (!intentHasSideEffects) {
                            setFlowStatuses(function (previousFlowStatuses) {
                                var _a;
                                return (__assign(__assign({}, previousFlowStatuses), (_a = {}, _a[flow.sid] = { pending: false, failed: false }, _a)));
                            });
                        }
                    }, function () {
                        setFlowStatuses(function (previousFlowStatuses) {
                            var _a;
                            return (__assign(__assign({}, previousFlowStatuses), (_a = {}, _a[flow.sid] = { pending: false, failed: true }, _a)));
                        });
                    });
                }
                statuses[flow.sid] = { pending: pending, failed: false };
                return statuses;
            }, {}));
        }
    }, [socket, fetching]);
    var blockIntention = useMemo(function () {
        if (fetching) {
            return function () { };
        }
        return function (uid, transitions) {
            var _a;
            if (blocks[uid]) {
                var block = blocks[uid];
                blockIntent({
                    uid: uid,
                    sid: block.sid,
                    version: block.version,
                    transitions: transitions,
                });
                if (optimisticUpdates && block.state.active) {
                    updateBlockState((_a = {},
                        _a[uid] = __assign(__assign({}, block), { state: {
                                entered: true,
                                exited: true,
                                active: false,
                            } }),
                        _a));
                }
            }
        };
    }, [fetching, blockIntent, blocks]);
    var flowIntention = useMemo(function () {
        if (fetching) {
            return {
                start: function () { },
                reset: function () { },
                finish: function () { },
                stop: function () { },
            };
        }
        return {
            start: function (sid, version) {
                flowIntent({ sid: sid, version: version, intent: 'start' });
            },
            reset: function (sid, version) {
                flowIntent({ sid: sid, version: version, intent: 'reset' });
            },
            finish: function (sid, version) {
                flowIntent({ sid: sid, version: version, intent: 'finish' });
            },
            stop: function (sid, version) {
                flowIntent({ sid: sid, version: version, intent: 'stop' });
            },
        };
    }, [fetching, flows]);
    return (jsx(DoptContext.Provider, __assign({ value: {
            fetching: fetching,
            flowStatuses: flowStatuses,
            flowBlocks: flowBlocks,
            flows: flows,
            flowIntention: flowIntention,
            blocks: blocks,
            blockUidBySid: blockUidBySid,
            blockIntention: blockIntention,
            blockFields: blockFields,
            log: log,
        } }, { children: children })));
}

export { DoptProvider, URL_PREFIX, useBlock, useContainer, useDoptInitialized, useFlow, useFlowStatus, withBlock, withFlow };
